import { MarketingCta } from '../../../domain/marketing-cta';
import {
  CreateMarketingPageItemButtonApiModel,
  CreateMarketingPageItemCtaRequestApiModel,
  DescriptionStyleApiModel,
  MarketingPageItemCtaApiModel,
  TitleStyleApiModel,
} from '@cxb/editor-stb-api';
import { isTruthy, mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  descriptionLocalizedMapperDomainToApi,
  localizedBackgroundImagesMapper,
  mapDomainButtonToApi,
  mapDomainCtaLayoutToApi,
  platformTargetingMapper,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
  titleLocalizedFromHeadlineMapper,
} from '../../mapping/create-tray-domain-to-api';
import { TrayWithOptionalSecondaryButton, TrayWithPrimaryButton } from '../../../domain/tray-common';
import {
  backgroundImagesMapperApiToDomain,
  commonMapper,
  descriptionLocalizedMapperApiToDomain,
  headlineTextMapper,
  mapApiButtonToDomain,
  mapApiCtaLayoutToDomain,
  scheduledDateMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { MarketingTrayButtonType } from '../../../domain/marketing-tray-button';
import { MarketingCtaTrayFormViewModel } from './marketing-cta-tray-form.view-model';
import { SchedulingType } from '../../../shared/enums/scheduling-type.enum';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  mapDomainButtonToButtonViewModel,
  mapLocalizedDomainImagesToPlatformLocalizedViewModelImage,
  mapMarketingItemLayoutToTraySelectorType,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  mapMarketingButtonToDomainButton,
  mapTraySelectorTypeToMarketingItemLayout,
  mapViewModelImagesToLocalizedDomainImages,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';
// 'MarketingCta'

/**
 * DOMAIN -> API
 */

const primaryButtonMapperDomainToApi = <T extends TrayWithPrimaryButton>(
  tray: T
): { primaryButton: CreateMarketingPageItemButtonApiModel } => {
  return {
    primaryButton: mapDomainButtonToApi(tray.primaryButton),
  };
};

const optionalSecondaryButtonMapperDomainToApi = <T extends TrayWithOptionalSecondaryButton>(
  tray: T
): { secondaryButton: CreateMarketingPageItemButtonApiModel } => {
  return {
    secondaryButton: tray.hasSecondaryButton ? mapDomainButtonToApi(tray.secondaryButton) : undefined,
  };
};

export const mapDomainCtaToApi = (
  cta: MarketingCta,
  pageVersionId: string,
  tenantId?: string
): CreateMarketingPageItemCtaRequestApiModel => {
  const commonProperties = mapperPipe(
    cta,
    titleLocalizedFromHeadlineMapper,
    descriptionLocalizedMapperDomainToApi,
    targetingMapperDomainToApi,
    scheduledDateMapperDomainToApi,
    primaryButtonMapperDomainToApi,
    optionalSecondaryButtonMapperDomainToApi,
    localizedBackgroundImagesMapper,
    platformTargetingMapper
  );

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    layout: mapDomainCtaLayoutToApi(cta.template),
    name: cta.titleEnglish,
    titleStyle: TitleStyleApiModel.Light,
    descriptionStyle: DescriptionStyleApiModel.Light,
    tenantId,
  }));
};

/**
 * API -> DOMAIN
 */
const optionalSecondaryButtonMapperApiToDomain = <T extends { secondaryButton?: CreateMarketingPageItemButtonApiModel }>(
  tray: T
): TrayWithOptionalSecondaryButton => {
  return {
    secondaryButton: tray.secondaryButton ? mapApiButtonToDomain(tray.secondaryButton, MarketingTrayButtonType.Secondary) : undefined,
    hasSecondaryButton: isTruthy(tray.secondaryButton),
  };
};

const primaryButtonMapperApiToDomain = <T extends { primaryButton: CreateMarketingPageItemButtonApiModel }>(
  tray: T
): TrayWithPrimaryButton => {
  return {
    primaryButton: mapApiButtonToDomain(tray.primaryButton, MarketingTrayButtonType.Primary),
  };
};

export const mapApiCtaToDomain = (cta: MarketingPageItemCtaApiModel): MarketingCta => {
  return remainderMapper(
    mapperPipe(
      cta,
      commonMapper,
      optionalSecondaryButtonMapperApiToDomain,
      primaryButtonMapperApiToDomain,
      headlineTextMapper,
      descriptionLocalizedMapperApiToDomain,
      backgroundImagesMapperApiToDomain,
      scheduledDateMapperApiToDomain,
      targetingMapperDomainToApi
    ),
    () => ({
      type: 'MarketingCta',
      titleEnglish: cta.name,
      template: mapApiCtaLayoutToDomain(cta.layout),
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapDomainCTAToViewModel = (cta: MarketingCta): MarketingCtaTrayFormViewModel => ({
  id: cta.id,
  description: { en: cta.descriptionTextEnglish, ar: cta.descriptionTextArabic },
  hasSecondaryButton: cta.hasSecondaryButton,
  headlineText: { en: cta.headlineTextEnglish, ar: cta.headlineTextArabic },
  backgroundImages: mapLocalizedDomainImagesToPlatformLocalizedViewModelImage(cta.backgroundImages),
  primaryButton: mapDomainButtonToButtonViewModel(cta.primaryButton),
  scheduling: {
    schedulingType: cta.scheduledFrom || cta.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
    scheduledFrom: cta.scheduledFrom ? new Date(cta.scheduledFrom).toISOString() : undefined,
    scheduledTo: cta.scheduledTo ? new Date(cta.scheduledTo).toISOString() : undefined,
  },
  secondaryButton: cta.hasSecondaryButton ? mapDomainButtonToButtonViewModel(cta.secondaryButton) : undefined,
  template: mapMarketingItemLayoutToTraySelectorType(cta.template),
  moduleName: cta.titleEnglish,
  type: TrayFormViewModelType.CTA,
  userSegmentIds: cta.userSegmentIds,
  countryIds: cta.countryIds,
});

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapViewModelCtaToDomain = (
  marketingCtaViewModel: MarketingCtaTrayFormViewModel,
  platformIds: Record<PlatformType, string>
): MarketingCta => {
  return {
    id: marketingCtaViewModel.id,
    descriptionTextArabic: marketingCtaViewModel.description.ar,
    descriptionTextEnglish: marketingCtaViewModel.description.en,
    hasSecondaryButton: marketingCtaViewModel.hasSecondaryButton,
    headlineTextArabic: marketingCtaViewModel.headlineText.ar,
    headlineTextEnglish: marketingCtaViewModel.headlineText.en,
    backgroundImages: mapViewModelImagesToLocalizedDomainImages(marketingCtaViewModel.backgroundImages, platformIds),
    primaryButton: mapMarketingButtonToDomainButton(marketingCtaViewModel.primaryButton),
    secondaryButton: marketingCtaViewModel.hasSecondaryButton
      ? mapMarketingButtonToDomainButton(marketingCtaViewModel.secondaryButton)
      : undefined,
    scheduledFrom: marketingCtaViewModel.scheduling?.scheduledFrom
      ? new Date(marketingCtaViewModel.scheduling.scheduledFrom).getTime()
      : undefined,
    scheduledTo: marketingCtaViewModel.scheduling?.scheduledTo
      ? new Date(marketingCtaViewModel.scheduling.scheduledTo).getTime()
      : undefined,
    template: mapTraySelectorTypeToMarketingItemLayout(marketingCtaViewModel.template),
    titleEnglish: marketingCtaViewModel.moduleName,
    userSegmentIds: marketingCtaViewModel.userSegmentIds,
    countryIds: marketingCtaViewModel.countryIds,
    type: 'MarketingCta',
    order: undefined,
    visible: undefined,
    draggable: true,
    platformIds: Object.values(platformIds),
  };
};
